import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, formatApiError } from "../api/client";
import { toast } from "sonner";

/* V4.12 — Query Learning Center: unresolved terms, LLM suggestions,
   1-click alias adoption, run rollback, query analytics. */

const STATUS_TABS = [
  { key: "", label: "Alle" },
  { key: "open", label: "Offen" },
  { key: "pending_review", label: "Review" },
  { key: "auto_approved", label: "Auto" },
  { key: "approved", label: "Bestätigt" },
  { key: "rejected_llm", label: "LLM abgelehnt" },
  { key: "rejected_admin", label: "Abgelehnt" },
];

const STATUS_CLS = {
  open: "bg-zinc-800 text-zinc-300",
  pending_review: "bg-amber-500/15 text-amber-300",
  auto_approved: "bg-emerald-500/15 text-emerald-300",
  approved: "bg-emerald-500/25 text-emerald-200",
  rejected_llm: "bg-red-500/10 text-red-300/70",
  rejected_admin: "bg-red-500/15 text-red-300",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      month: "short", day: "2-digit",
    });
  } catch { return "—"; }
}

function StatCard({ label, value, sub, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3"
    >
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-xl text-zinc-100 font-medium">{value ?? "—"}</div>
      {sub && <div className="text-[11px] text-zinc-500">{sub}</div>}
    </div>
  );
}

function TopList({ title, items, render }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{title}</div>
      <div className="space-y-1">
        {items.map((it, i) => (
          <div key={i} className="flex justify-between text-sm">
            <span className="text-zinc-300 truncate pr-2">{render(it)}</span>
            <span className="text-zinc-500 font-mono">{it.n ?? it.hits}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TermRow({ term, onApprove, onReject, busy }) {
  const sug = term.suggestion || {};
  const [canonical, setCanonical] = useState(sug.canonical || "");
  const actionable = ["open", "pending_review", "rejected_llm"].includes(term.status);
  return (
    <tr data-testid={`ql-term-${term.ulid}`} className="border-t border-zinc-800/70">
      <td className="px-3 py-2">
        <div className="text-zinc-100 font-medium">{term.raw_display}</div>
        <div className="text-[11px] text-zinc-500">
          {term.kind} · {term.vertical || "—"}{sug.lang ? ` · ${sug.lang}` : ""}
        </div>
      </td>
      <td className="px-3 py-2 text-center text-zinc-300 font-mono">{term.seen_count || 0}</td>
      <td className="px-3 py-2 text-[11px] text-zinc-500 whitespace-nowrap">
        {fmtDate(term.created_at)} → {fmtDate(term.last_seen_at)}
      </td>
      <td className="px-3 py-2">
        {actionable ? (
          <input
            data-testid={`ql-canonical-input-${term.ulid}`}
            value={canonical}
            onChange={(e) => setCanonical(e.target.value)}
            placeholder="canonical…"
            className="w-full bg-zinc-950/60 border border-zinc-800 rounded-md px-2 py-1 text-sm text-zinc-200"
          />
        ) : (
          <span className="text-sm text-emerald-300">{sug.canonical || "—"}</span>
        )}
        {sug.effective_confidence != null && (
          <div className="text-[11px] text-zinc-500 mt-0.5">
            Confidence {(sug.effective_confidence * 100).toFixed(0)} %
            {sug.reasoning ? ` — ${sug.reasoning}` : ""}
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <span className={`px-2 py-0.5 rounded text-[11px] ${STATUS_CLS[term.status] || "bg-zinc-800 text-zinc-300"}`}>
          {term.status}
        </span>
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        {actionable && (
          <div className="flex gap-1.5">
            <button
              data-testid={`ql-approve-${term.ulid}`}
              disabled={busy || !canonical.trim()}
              onClick={() => onApprove(term, canonical.trim())}
              className="px-2.5 py-1 rounded-md bg-emerald-500/15 text-emerald-300 text-xs hover:bg-emerald-500/25 disabled:opacity-40"
            >
              Übernehmen
            </button>
            <button
              data-testid={`ql-reject-${term.ulid}`}
              disabled={busy}
              onClick={() => onReject(term)}
              className="px-2.5 py-1 rounded-md bg-red-500/10 text-red-300 text-xs hover:bg-red-500/20 disabled:opacity-40"
            >
              Ablehnen
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function AdminQueryLearningPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);

  const { data: stats } = useQuery({
    queryKey: ["ql-stats"],
    queryFn: async () => (await apiClient.get("/admin/query-learning/stats")).data,
  });
  const { data: terms } = useQuery({
    queryKey: ["ql-terms", status],
    queryFn: async () =>
      (await apiClient.get("/admin/query-learning/terms", {
        params: { status: status || undefined, limit: 100 },
      })).data,
  });
  const { data: runs } = useQuery({
    queryKey: ["ql-runs"],
    queryFn: async () => (await apiClient.get("/admin/query-learning/runs")).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["ql-stats"] });
    qc.invalidateQueries({ queryKey: ["ql-terms"] });
    qc.invalidateQueries({ queryKey: ["ql-runs"] });
  };

  const approve = async (term, canonical) => {
    setBusy(true);
    try {
      await apiClient.post(`/admin/query-learning/terms/${term.ulid}/approve`, { canonical });
      toast.success(`Alias übernommen: ${term.raw_display} → ${canonical}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const reject = async (term) => {
    setBusy(true);
    try {
      await apiClient.post(`/admin/query-learning/terms/${term.ulid}/reject`);
      toast.success(`Abgelehnt: ${term.raw_display}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const runLoop = async () => {
    setRunning(true);
    try {
      const { data } = await apiClient.post("/admin/query-learning/run", { limit: 20 });
      toast.success(
        `LLM-Run: ${data.auto} auto · ${data.pending} pending · ${data.rejected} abgelehnt`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); } finally { setRunning(false); }
  };
  const rollback = async (run) => {
    if (!window.confirm(`Run ${run.ulid.slice(0, 8)}… wirklich zurückrollen?`)) return;
    try {
      const { data } = await apiClient.post("/admin/query-learning/rollback", { run_id: run.ulid });
      toast.success(`Rollback: ${data.aliases_deactivated} Aliase deaktiviert`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const s = stats || {};
  const byStatus = s.terms_by_status || {};

  return (
    <div data-testid="admin-query-learning" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl text-zinc-100 font-medium">Query Learning</h1>
          <div className="text-xs text-zinc-500">
            Resolver {s.resolver_version || "—"} · Learning-Store & Alias-Freigabe
          </div>
        </div>
        <button
          data-testid="ql-run-llm"
          disabled={running}
          onClick={runLoop}
          className="px-4 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 text-sm hover:bg-emerald-500/25 disabled:opacity-50"
        >
          {running ? "LLM analysiert…" : "LLM-Vorschläge generieren"}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard testid="ql-stat-open" label="Offen" value={byStatus.open || 0} />
        <StatCard testid="ql-stat-pending" label="Review" value={byStatus.pending_review || 0} />
        <StatCard testid="ql-stat-auto" label="Auto (7 Tage)" value={s.auto_approved_7d || 0} />
        <StatCard testid="ql-stat-confirmed" label="Bestätigte Aliase" value={s.confirmed_aliases_total || 0} />
        <StatCard testid="ql-stat-new7" label="Neue Begriffe" value={s.new_terms_7d || 0} sub={`30 Tage: ${s.new_terms_30d || 0}`} />
        <StatCard testid="ql-stat-events" label="Suchanfragen (7 T.)" value={(s.events_7d || {}).total || 0} sub={`30 Tage: ${(s.events_30d || {}).total || 0}`} />
        <StatCard testid="ql-stat-expanded" label="Erweitert (7 T.)" value={`${(((s.events_7d || {}).expanded_rate || 0) * 100).toFixed(0)} %`} sub="Fallback-Kaskade aktiv" />
        <StatCard testid="ql-stat-zerohit" label="Ohne Treffer (7 T.)" value={`${(((s.events_7d || {}).zero_hit_rate || 0) * 100).toFixed(0)} %`} />
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`ql-tab-${t.key || "all"}`}
            onClick={() => setStatus(t.key)}
            className={`px-3 py-1.5 rounded-md text-xs ${
              status === t.key
                ? "bg-emerald-500/15 text-emerald-200 border border-emerald-500/40"
                : "bg-zinc-900/60 text-zinc-400 border border-zinc-800 hover:text-zinc-200"
            }`}
          >
            {t.label}
            {t.key && byStatus[t.key] ? ` (${byStatus[t.key]})` : ""}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm" data-testid="ql-terms-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
              <th className="px-3 py-2">Begriff</th>
              <th className="px-3 py-2 text-center">Anzahl</th>
              <th className="px-3 py-2">Zeitraum</th>
              <th className="px-3 py-2">Vorschlag / Alias</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {(terms?.items || []).map((t) => (
              <TermRow key={t.ulid} term={t} onApprove={approve} onReject={reject} busy={busy} />
            ))}
            {(!terms || terms.items.length === 0) && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-zinc-500">
                Keine Einträge — die Suche hat aktuell alles auflösen können.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(runs?.items || []).length > 0 && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Learning-Runs</div>
          <div className="space-y-1.5">
            {runs.items.map((r) => (
              <div key={r.ulid} className="flex items-center justify-between text-sm" data-testid={`ql-run-${r.ulid}`}>
                <span className="text-zinc-400">
                  {fmtDate(r.started_at)} · {r.llm_model} · {r.terms} Begriffe →{" "}
                  <span className="text-emerald-300">{r.auto} auto</span> ·{" "}
                  <span className="text-amber-300">{r.pending} pending</span> · {r.rejected} abgelehnt
                </span>
                {r.rolled_back ? (
                  <span className="text-[11px] text-red-300/70">zurückgerollt</span>
                ) : r.auto > 0 ? (
                  <button
                    data-testid={`ql-rollback-${r.ulid}`}
                    onClick={() => rollback(r)}
                    className="px-2.5 py-1 rounded-md bg-red-500/10 text-red-300 text-xs hover:bg-red-500/20"
                  >
                    Rollback
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <TopList title="Top Marken (30 T.)" items={s.top_makes_30d} render={(it) => it.value} />
        <TopList title="Top Features (30 T.)" items={s.top_features_30d} render={(it) => it.value} />
        <TopList title="Top Regionen (30 T.)" items={s.top_regions_30d} render={(it) => it.value} />
        <TopList title="Top bestätigte Aliase" items={s.top_confirmed_aliases}
          render={(it) => `${it.raw_display || it.raw_norm} → ${it.canonical}`} />
      </div>
    </div>
  );
}
